import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to track network status and connection quality
 * @returns {Object} Network status information
 */
const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connection, setConnection] = useState({
    effectiveType: '',
    downlink: 0,
    rtt: 0,
    saveData: false,
    type: 'unknown'
  });
  const [lastOffline, setLastOffline] = useState(null);
  const [offlineDuration, setOfflineDuration] = useState(0);
  const [isConnectionSlow, setIsConnectionSlow] = useState(false);
  const [offlineQueuedActions, setOfflineQueuedActions] = useState([]);

  // Update connection information when it changes
  const updateConnectionInfo = useCallback(() => {
    if ('connection' in navigator) {
      const connectionInfo = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connectionInfo) {
        setConnection({
          effectiveType: connectionInfo.effectiveType || '',
          downlink: connectionInfo.downlink || 0,
          rtt: connectionInfo.rtt || 0,
          saveData: connectionInfo.saveData || false,
          type: connectionInfo.type || 'unknown'
        });
        
        // Consider connection slow if effectiveType is '2g' or 'slow-2g' or RTT is high
        const slowConnection = 
          connectionInfo.effectiveType === '2g' || 
          connectionInfo.effectiveType === 'slow-2g' ||
          (connectionInfo.rtt > 600); // RTT > 600ms is considered slow
          
        setIsConnectionSlow(slowConnection);
      }
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (lastOffline) {
        const duration = (Date.now() - lastOffline) / 1000; // in seconds
        setOfflineDuration(duration);
        setLastOffline(null);
        
        // Process any queued actions
        if (offlineQueuedActions.length > 0) {
          console.log(`Processing ${offlineQueuedActions.length} queued actions`);
          // Here you could implement logic to retry failed actions
          // For now, we'll just clear the queue
          setOfflineQueuedActions([]);
        }
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setLastOffline(Date.now());
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check connection info initially and set up change listener if available
    updateConnectionInfo();
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.addEventListener('change', updateConnectionInfo);
      }
    }

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if ('connection' in navigator) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection) {
          connection.removeEventListener('change', updateConnectionInfo);
        }
      }
    };
  }, [lastOffline, offlineQueuedActions, updateConnectionInfo]);

  /**
   * Queue an action to be performed when back online
   * @param {Function} action - The action to perform
   * @param {string} id - Unique identifier for the action (optional)
   * @returns {Promise} Resolves when the action is queued
   */
  const queueAction = useCallback((action, id) => {
    return new Promise((resolve, reject) => {
      if (isOnline) {
        // If online, execute immediately
        try {
          const result = action();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      } else {
        // If offline, add to queue
        const actionWithId = { 
          id: id || Date.now().toString(),
          action,
          timestamp: new Date().toISOString()
        };
        
        setOfflineQueuedActions(prev => [...prev, actionWithId]);
        resolve({ queued: true, id: actionWithId.id });
      }
    });
  }, [isOnline]);

  /**
   * Check if the current connection is suitable for the given data requirements
   * @param {Object} requirements - The data requirements
   * @param {number} requirements.minSpeed - Minimum download speed in Mbps
   * @param {number} requirements.maxLatency - Maximum acceptable latency in ms
   * @returns {boolean} True if the connection meets the requirements
   */
  const isConnectionSuitable = useCallback(({ minSpeed = 0.5, maxLatency = 1000 } = {}) => {
    if (!isOnline) return false;
    
    const { downlink, rtt } = connection;
    return downlink >= minSpeed && rtt <= maxLatency;
  }, [connection, isOnline]);

  return {
    isOnline,
    isOffline: !isOnline,
    isConnectionSlow,
    connection,
    lastOffline,
    offlineDuration,
    offlineQueuedCount: offlineQueuedActions.length,
    queueAction,
    isConnectionSuitable,
    // Additional helper methods can be added here
  };
};

export default useNetworkStatus;
